import { LuMoveHorizontal, LuCirclePlay, LuQuote } from 'react-icons/lu'
import { useState } from 'react'
import { cn } from '@/lib/cn'
import { useReveal } from '@/hooks/useReveal'
import {
  testimonialOne,
  testimonialTwo,
  testimonialOnePoster,
  testimonialTwoPoster,
  testimonialThree,
  testimonialThreePoster,
  testimonialFour,
  testimonialFourPoster,
  testimonialFive,
  testimonialFivePoster,
  testimonialSix,
  testimonialSixPoster,
} from '@/assets/siteMedia'

const testimonials = [
  {
    quote:
      'Meeting Kandake and participating in her herbal courses has empowered me to reconnect with my spirit, mind and body. Her teaching through ancient knowledge, visuals and storytelling is truly life changing.',
    author: 'Sandra Salazar',
    role: 'Course Participant',
  },
  {
    quote:
      "The wealth of knowledge gained has been enlightening and has enhanced all prior knowledge I had in health. Kandake's presentation is professional, articulate, spiritual and passionate.",
    author: 'Dawn Patrick',
    role: 'Course Participant',
  },
  {
    quote:
      'The course opened my eyes to the many aspects of holistic health and wellbeing. It ignited a higher respect for the plant kingdom and nature around me.',
    author: 'Rayna',
    role: 'Workshop Attendee',
  },
  {
    quote:
      "I learnt so much about women's health and natural remedies. Kandake is an amazing herbalist and teacher who ensures everyone truly understands.",
    author: 'Jackie',
    role: 'Consultation Client',
  },
]

const videoTestimonials = [
  { src: testimonialOne, poster: testimonialOnePoster, label: 'Course participant testimonial' },
  { src: testimonialTwo, poster: testimonialTwoPoster, label: 'Community participant testimonial' },
  { src: testimonialThree, poster: testimonialThreePoster, label: 'Client video testimonial 3' },
  { src: testimonialFour, poster: testimonialFourPoster, label: 'Client video testimonial 4' },
  { src: testimonialFive, poster: testimonialFivePoster, label: 'Client video testimonial 5' },
  { src: testimonialSix, poster: testimonialSixPoster, label: 'Client video testimonial 6' },
]

export const TestimonialsSection = () => {
  const { ref, visible } = useReveal()
  const [activeVideo, setActiveVideo] = useState(0)
  const selectedVideo = videoTestimonials[activeVideo]

  return (
    <section
      aria-labelledby="testimonials-heading"
      className="overflow-hidden border-t border-base-200 bg-base-50 py-16 sm:py-20 lg:py-24"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h2
              id="testimonials-heading"
              className="font-display text-3xl leading-tight tracking-tight text-base-900 sm:text-4xl lg:text-5xl"
            >
              Personal stories.
              <br />
              <span className="text-green-700">Shared in their own words.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-base-600">
            Meet the people behind the experiences. Hear their stories of learning, connection, and
            wellbeing with Kandake.
          </p>
        </div>

        <div
          ref={ref}
          className={cn(
            'overflow-hidden rounded-3xl border border-base-200 bg-white shadow-sm motion-safe:transition-all motion-safe:duration-700',
            visible
              ? 'opacity-100 translate-y-0'
              : 'motion-safe:opacity-0 motion-safe:translate-y-6',
          )}
        >
          <div className="grid lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            <figure className="min-w-0 bg-green-950">
              <video
                key={selectedVideo.src}
                src={selectedVideo.src}
                poster={selectedVideo.poster}
                controls
                preload="none"
                playsInline
                className="aspect-[9/16] max-h-[70svh] w-full object-contain sm:aspect-[4/3] sm:max-h-[540px]"
                aria-label={selectedVideo.label}
              />
              <figcaption className="flex items-center justify-between gap-4 border-t border-white/10 px-6 py-5 text-white">
                <div>
                  <p className="mb-1 text-[0.65rem] uppercase tracking-[0.18em] text-green-200">
                    In their own words
                  </p>
                  <p className="font-display text-lg">
                    Community story {String(activeVideo + 1).padStart(2, '0')}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-green-200">
                  {activeVideo + 1} / {videoTestimonials.length}
                </span>
              </figcaption>
            </figure>

            <div className="flex min-h-0 min-w-0 flex-col p-5 sm:p-6">
              <div className="mb-5 flex items-center justify-between gap-3">
                <h3 className="font-display text-xl text-base-900">Watch their stories</h3>
                <span className="rounded-full bg-green-50 px-3 py-1 text-xs text-green-700">
                  {videoTestimonials.length} videos
                </span>
              </div>
              <p className="mb-3 flex items-center gap-2 text-xs text-base-500 lg:hidden">
                Swipe to browse
                <LuMoveHorizontal aria-hidden="true" className="h-4 w-4 shrink-0" />
              </p>
              <div className="flex snap-x snap-proximity gap-3 overflow-x-auto pb-2 lg:max-h-[510px] lg:snap-none lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:pr-1">
                {videoTestimonials.map((video, i) => (
                  <button
                    key={video.src}
                    type="button"
                    onClick={() => setActiveVideo(i)}
                    aria-pressed={activeVideo === i}
                    aria-label={`Select community story ${i + 1}`}
                    className={cn(
                      'flex w-56 shrink-0 snap-start items-center gap-4 rounded-2xl border p-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-inset lg:w-full',
                      activeVideo === i
                        ? 'border-green-200 bg-green-50'
                        : 'border-base-100 bg-white hover:border-base-200 hover:bg-base-50',
                    )}
                  >
                    <img
                      src={video.poster}
                      alt=""
                      loading="lazy"
                      className="h-16 w-14 shrink-0 rounded-lg bg-base-100 object-cover"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-base-900">
                        Community story {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="mt-1 block text-xs text-green-700">
                        {activeVideo === i ? 'Selected story' : 'Select to watch'}
                      </span>
                    </span>
                    <LuCirclePlay aria-hidden="true" className="h-7 w-7 shrink-0 text-green-700" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
          <h3 className="font-display text-2xl text-base-900">A few words from our community</h3>
          <p className="text-sm text-base-500">Experiences shared with us</p>
        </div>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {testimonials.map((testimonial) => (
            <figure
              key={testimonial.author}
              className="flex flex-col rounded-2xl border border-base-200 bg-white p-6 sm:p-8"
            >
              <LuQuote aria-hidden="true" className="h-8 w-8 text-green-300" />
              <blockquote className="mb-7 mt-3 flex-1 text-sm leading-7 text-base-700">
                {testimonial.quote}
              </blockquote>
              <figcaption className="flex items-center gap-3 border-t border-base-100 pt-5">
                <span
                  aria-hidden="true"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-base-100 font-display text-base text-green-800"
                >
                  {testimonial.author
                    .split(' ')
                    .map((name) => name[0])
                    .join('')}
                </span>
                <div>
                  <p className="text-sm font-medium text-base-900">{testimonial.author}</p>
                  <p className="mt-0.5 text-xs text-base-500">{testimonial.role}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  )
}
